/**
 * Dependent Field Service
 * Manages field dependencies and cascading updates
 */

import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IFormField, DataFetchContext } from '../models';
import { DataFetchService } from './data-fetch.service';
import { FORM_CONFIG } from '../constants';

/**
 * Context for dependency setup
 */
export interface IDependencyContext {
  companyId: number;
  branchId: number;
  userId: number;
  masterId?: number;
  masterType?: string;
}

/**
 * Service for managing field dependencies
 */
@Injectable({ providedIn: 'root' })
export class DependentFieldService {
  constructor(private dataFetchService: DataFetchService) {}
  
  /**
   * Setup all dependent field listeners
   * @param fields All form fields
   * @param formGroup The form group
   * @param context User/company context
   * @param destroy$ Observable for cleanup
   */
  setupDependencies(
    fields: IFormField[],
    formGroup: FormGroup,
    context: IDependencyContext,
    destroy$: Subject<void>
  ): void {
    console.log('[DependentFieldService] Setting up dependencies for', fields.length, 'fields');
    
    let setupCount = 0;
    
    fields.forEach(field => {
      // Skip fields without data fetching
      if (!field.dataFetchType) return;
      
      // Setup strategy-specific logic (like JSExecutor auto-subscriptions)
      const dataContext = DataFetchContext.create(
        field,
        formGroup,
        context,
        fields
      );
      
      this.dataFetchService.setupField(dataContext);
      
      // If field has dependencies, setup change listeners
      if (field.dependentOn) {
        this.setupDependentListener(field, formGroup, context, fields, destroy$);
        setupCount++;
      }
    });
    
    console.log(`[DependentFieldService] Setup complete. ${setupCount} dependent fields configured.`);
  }
  
  /**
   * Setup listener for a specific dependent field
   */
  private setupDependentListener(
    field: IFormField,
    formGroup: FormGroup,
    context: IDependencyContext,
    allFields: IFormField[],
    destroy$: Subject<void>
  ): void {
    const dependentControl = formGroup.get(field.dependentOn!);
    
    if (!dependentControl) {
      console.warn(`[DependentFieldService] Dependent field "${field.dependentOn}" not found for field "${field.dbFeildName}"`);
      return;
    }
    
    // Subscribe to dependent field changes
    dependentControl.valueChanges.pipe(
      debounceTime(FORM_CONFIG.VALUE_CHANGE_DEBOUNCE_TIME),
      distinctUntilChanged(),
      takeUntil(destroy$)
    ).subscribe(value => {
      this.handleDependentChange(field, value, formGroup, context, allFields);
    });
  }
  
  /**
   * Handle change in dependent field
   */
  private handleDependentChange(
    field: IFormField,
    dependentValue: any,
    formGroup: FormGroup,
    context: IDependencyContext,
    allFields: IFormField[]
  ): void {
    // If dependent value is empty, clear the field
    if (!dependentValue || dependentValue === '') {
      this.clearField(field, formGroup);
      return;
    }
    
    // Create fetch context
    const fetchContext = DataFetchContext.create(
      field,
      formGroup,
      context,
      allFields
    ).withDependentValue(dependentValue);
    
    // Fetch new data
    this.dataFetchService.fetchData(fetchContext).subscribe({
      next: (result) => {
        this.updateField(field, result, formGroup);
      },
      error: (error) => {
        console.error(`[DependentFieldService] Error fetching data for field "${field.dbFeildName}":`, error);
        this.clearField(field, formGroup);
      }
    });
  }
  
  /**
   * Update field with fetched data
   */
  private updateField(field: IFormField, result: any, formGroup: FormGroup): void {
    if (!field.dbFeildName) return;
    
    // If patchDataInto is specified, patch multiple fields
    if (field.patchDataInto && result && typeof result === 'object') {
      this.patchMultipleFields(field.patchDataInto, result, formGroup);
    } else {
      // Otherwise, just set the field value
      const control = formGroup.get(field.dbFeildName);
      if (control) {
        control.setValue(result, { emitEvent: false });
      }
    }
  }
  
  /**
   * Clear field value
   */
  private clearField(field: IFormField, formGroup: FormGroup): void {
    if (!field.dbFeildName) return;
    
    const control = formGroup.get(field.dbFeildName);
    if (control) {
      control.setValue(null, { emitEvent: false });
      
      // Also clear any options if it's a select field
      if (field.options) {
        field.options = [];
      }
    }
  }
  
  /**
   * Patch multiple fields from result
   * Format: "targetField1:sourceProperty1,targetField2:sourceProperty2"
   */
  private patchMultipleFields(
    patchConfig: string,
    data: any,
    formGroup: FormGroup
  ): void {
    if (!data || typeof data !== 'object') return;
    
    const mappings = patchConfig.split(',').map(m => m.trim());
    
    mappings.forEach(mapping => {
      const [targetField, sourceProperty] = mapping.split(':').map(s => s.trim());
      
      if (targetField && sourceProperty && data[sourceProperty] !== undefined) {
        const control = formGroup.get(targetField);
        if (control) {
          control.setValue(data[sourceProperty], { emitEvent: false });
        }
      }
    });
  }
  
  /**
   * Cleanup dependent field subscriptions
   */
  cleanup(
    fields: IFormField[],
    formGroup: FormGroup,
    context: IDependencyContext
  ): void {
    fields.forEach(field => {
      if (field.dataFetchType) {
        const dataContext = DataFetchContext.create(
          field,
          formGroup,
          context
        );
        
        this.dataFetchService.cleanupField(dataContext);
      }
    });
    
    console.log('[DependentFieldService] Cleanup complete');
  }
}
